import * as Headless from '@headlessui/react';
import { clsx } from 'clsx';
import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { TouchTarget } from './button';
import { Link } from './link';

type AvatarProps = {
  src?: string | null;
  square?: boolean;
  initials?: string;
  alt?: string;
  className?: string;
  iconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
};

export function Avatar({
  src = null,
  square = false,
  initials,
  alt = '',
  className,
  iconSize = 'xl',
  ...props
}: AvatarProps & React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      data-slot="avatar"
      {...props}
      className={twMerge(
        clsx(
          // Basic layout
          'tw:inline-grid tw:shrink-0 tw:align-middle tw:overflow-hidden tw:[--avatar-radius:20%] tw:[--ring-opacity:20%] tw:*:col-start-1 tw:*:row-start-1',
          'tw:outline-1 tw:-outline-offset-1 tw:outline-zinc-200 tw:dark:outline-white/[--ring-opacity]',
          // Add the correct border radius
          square
            ? 'tw:rounded-[--avatar-radius] tw:*:rounded-[--avatar-radius]'
            : 'tw:rounded-full tw:*:rounded-full',
          className
        )
      )}
    >
      {initials ? (
        <svg
          className="tw:size-full tw:select-none tw:fill-current tw:p-[5%] tw:text-[48px] tw:font-medium tw:uppercase"
          viewBox="0 0 100 100"
          aria-hidden={alt ? undefined : 'true'}
        >
          {alt && <title>{alt}</title>}
          <text
            x="50%"
            y="50%"
            alignmentBaseline="middle"
            dominantBaseline="middle"
            textAnchor="middle"
            dy=".125em"
          >
            {initials}
          </text>
        </svg>
      ) : src ? (
        <img className="tw:size-full tw:object-cover" src={src} alt={alt} />
      ) : (
        <div className="tw:rounded-lg tw:bg-zinc-200 tw:flex tw:items-center tw:justify-center">
          <i className={`fas fa-user tw:text-zinc-400 fa-${iconSize}`} />
        </div>
      )}
    </span>
  );
}

export const AvatarButton = forwardRef(function AvatarButton(
  {
    src,
    square = false,
    initials,
    alt,
    className,
    ...props
  }: AvatarProps &
    (
      | Omit<Headless.ButtonProps, 'as' | 'className'>
      | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>
    ),
  ref: React.ForwardedRef<HTMLElement>
) {
  const classes = clsx(
    className,
    square ? 'tw:rounded-[20%]' : 'tw:rounded-full',
    'tw:relative tw:inline-grid tw:focus:outline-none tw:data-[focus]:outline tw:data-[focus]:outline-2 tw:data-[focus]:outline-offset-2 tw:data-[focus]:outline-blue-500'
  );

  return 'href' in props ? (
    <Link {...props} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
      <TouchTarget>
        <Avatar src={src} square={square} initials={initials} alt={alt} />
      </TouchTarget>
    </Link>
  ) : (
    <Headless.Button {...props} className={classes} ref={ref}>
      <TouchTarget>
        <Avatar src={src} square={square} initials={initials} alt={alt} />
      </TouchTarget>
    </Headless.Button>
  );
});
